import React, { useState, useEffect, useCallback } from 'react'
import styled from '@emotion/styled'
import { useParams, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { buildRoute } from '../../../pure-js/libs/RouteBuilder'
import RoutePath from '../../../pure-js/libs/RoutePath'
import { usePortalAuth } from '../hooks/usePortalAuth'
import { usePrivateLessonsForUser } from '../hooks/usePrivateLessonsForUser'
import { usePrivateLessonOrdersForUser } from '../hooks/usePrivateLessonOrdersForUser'
import { usePortalQueryClientId } from '../hooks/portalHostQueryGate'
import { useMounted } from '../hooks/useMounted'
import useAppState from '../hooks/useAppState'
import { getClient } from '../libs/DBApiHandler'
import { formatGrooverText, getGrooverText } from '../libs/GrooverTextRepository'
import { PrivateLessonOrder, Client } from '../../../pure-js/types/GrooverTypes'
import { OrdersTab } from '../components/OrdersTab'
import { OccasionsTab } from '../components/OccasionsTab'
import { getDefaultInstructorName, getLocationName } from '../../../pure-js/libs/PrivateLessonHelpers'
import GrooverText from '../components/GrooverText'
import { ReceiptSsnInputForm } from '../components/ReceiptSsnInputForm'
import { addSsnForPrivateLessonOrder } from '../libs/CloudFunctionsApiHandler'
import { getCurrencyForClient } from '../../../pure-js/libs/currencyHelper'
import { useReceiptSsn } from '../hooks/useReceiptSsn'
import { downloadReceiptPdf } from '../libs/downloadReceiptPdf'
import Box from '../components/Box'
import penIcon from '/public/penIcon.svg'
import plusIcon from '/public/plusIcon.svg'

const PageContainer = styled.div`
  background: #ffffff;
  color: #1f2937;
  width: 100%;
  min-width: 0;
`

const MainContent = styled.div`
  width: 100%;
  max-width: 100%;
  padding: 32px 40px;
  box-sizing: border-box;

  @media (min-width: 769px) {
    max-width: 1400px;
    margin: 0 auto;
  }

  @media (max-width: 768px) {
    padding: 16px 12px;
    min-width: 0;
  }
`

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  gap: 16px;
`

const LoadingSpinner = styled.div`
  width: 48px;
  height: 48px;
  border: 4px solid rgba(var(--portal-primary-rgb, 124, 58, 237), 0.1);
  border-top-color: var(--portal-primary, #7c3aed);
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`

const LoadingText = styled.div`
  font-size: 16px;
  color: #6b7280;
`

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  gap: 16px;
`

const ErrorIcon = styled.div`
  font-size: 48px;
  color: #ef4444;
`

const ErrorText = styled.div`
  font-size: 16px;
  color: #ef4444;
  text-align: center;
  max-width: 400px;
`

const RetryButton = styled.button`
  padding: 12px 24px;
  background: var(--portal-primary, #7c3aed);
  border: none;
  border-radius: 8px;
  color: #ffffff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: var(--portal-primary-dark, #6d28d9);
  }
`

const PageTitle = styled.h1`
  font-size: 32px;
  font-weight: 700;
  color: #111827;
  margin: 0 0 24px 0;

  @media (max-width: 768px) {
    font-size: 24px;
    margin-bottom: 16px;
  }
`

const TabsContainer = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 32px;
  border-bottom: 2px solid #e5e7eb;

  @media (max-width: 768px) {
    margin-bottom: 24px;
  }
`

const Tab = styled.button<{ active: boolean }>`
  padding: 12px 24px;
  background: transparent;
  border: none;
  border-bottom: 2px solid ${({ active }) => (active ? 'var(--portal-primary, #7c3aed)' : 'transparent')};
  color: ${({ active }) => (active ? 'var(--portal-primary, #7c3aed)' : '#6b7280')};
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  margin-bottom: -2px;

  &:hover {
    color: var(--portal-primary, #7c3aed);
  }

  @media (max-width: 768px) {
    padding: 10px 16px;
    font-size: 14px;
  }
`

// Receipt View Component
const ReceiptContainer = styled.div`
  max-width: 600px;
  margin: 0 auto;
`

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: transparent;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  color: #6b7280;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
  margin-bottom: 24px;

  &:hover {
    border-color: var(--portal-primary, #7c3aed);
    color: var(--portal-primary, #7c3aed);
  }
`

const BackIcon = styled.span`
  font-size: 16px;
`

const ReceiptHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  margin-bottom: 32px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e5e7eb;
`

const ReceiptTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: #111827;
  margin: 0;
`

const ReceiptRef = styled.div`
  font-size: 12px;
  color: #6b7280;
  font-family: monospace;
  margin-bottom: 8px;
  text-align: right;
`

const StatusBadge = styled.div<{ status: string }>`
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  text-transform: capitalize;

  ${({ status }) => {
    switch (status) {
      case 'active':
        return `
          background: rgba(34, 197, 94, 0.1);
          color: #22c55e;
        `
      case 'pending_payment':
        return `
          background: rgba(251, 191, 36, 0.1);
          color: #f59e0b;
        `
      case 'expired':
      case 'usedUp':
        return `
          background: rgba(156, 163, 175, 0.1);
          color: #9ca3af;
        `
      case 'refunded':
        return `
          background: rgba(239, 68, 68, 0.1);
          color: #ef4444;
        `
      default:
        return `
          background: rgba(156, 163, 175, 0.1);
          color: #9ca3af;
        `
    }
  }}
`

const RECEIPT_ELEMENT_ID = 'portal-order-receipt'

const ReceiptCard = styled.div`
  background: #fff;
  border-radius: 8px;
  padding: 12px 16px;
  border: 1px solid #929292;
  box-shadow: 0 0 8px 0 rgba(0, 0, 0, 0.1);
  margin-top: 12px;

  /* Hide elements when capturing for PDF (data-pdf-capture set in handleDownloadPDF) */
  &[data-pdf-capture='true'] .hide-in-pdf {
    display: none !important;
  }

  /* Smaller text for PDF on A4 (data-pdf-capture set in handleDownloadPDF) */
  &[data-pdf-capture='true'] {
    font-size: 8px;
    padding: 6px 10px;
  }
  &[data-pdf-capture='true'] * {
    font-size: inherit !important;
  }
`

const ReceiptTopRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 12px;
`

/* Seller section – separate from purchase details (enrollment-style) */
const SellerSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding-bottom: 10px;
  margin-bottom: 10px;
  border-bottom: 1px solid #929292;
`

const SellerLabel = styled.div`
  font-size: 13px;
  color: #374151;
  font-weight: 500;
  align-self: flex-start;
`

const SellerValue = styled.div`
  font-size: 13px;
  color: #111827;
  text-align: right;
  display: flex;
  flex-direction: column;
  gap: 2px;
  align-self: flex-start;
`

/* Dense rows for purchase details (enrollment-style) */
const ReceiptBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const ReceiptRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 13px;
`

const ReceiptRowLabel = styled.span`
  color: #374151;
  font-weight: 500;
`

const ReceiptRowValue = styled.span`
  color: #111827;
  font-weight: 600;
`

const DownloadPdfButton = styled.button`
  padding: 12px 20px;
  background: var(--portal-primary, #7c3aed);
  border: none;
  border-radius: 8px;
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: var(--portal-primary-dark, #6d28d9);
  }
`

const ReceiptActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`

const SsnToggle = styled.button`
  display: inline-flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: var(--portal-primary, #7c3aed);
  font-size: 14px;
  font-weight: 500;

  & * {
    color: inherit;
  }

  &:hover {
    opacity: 0.8;
  }
`

const SsnInputRow = styled.div`
  margin-bottom: 12px;
`

// Helper functions
const formatDate = (dateString: string): string => {
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })
}

const formatPrice = (priceInCents: number, currency?: string): string => {
  const price = priceInCents / 100
  return `${price.toFixed(2)} ${currency || 'SEK'}`
}

const STATUS_KEYS: Record<string, string> = {
  active: 'portalOrderStatusActive',
  pending_payment: 'portalOrderStatusPendingPayment',
  expired: 'portalOrderStatusExpired',
  usedUp: 'portalOrderStatusUsedUp',
  refunded: 'portalOrderStatusRefunded',
  deleted: 'portalOrderStatusDeleted'
}

const getStatusLabel = (status: string, getText: (key: string) => string): string => {
  const key = STATUS_KEYS[status]
  return key ? getText(key) : status
}

// Receipt View Component
interface OrderReceiptViewProps {
  order: PrivateLessonOrder
  client: Client | null
  onBack: () => void
  getText: (key: string) => string
}

const OrderReceiptView: React.FC<OrderReceiptViewProps> = ({ order, client, onBack, getText }) => {
  const instructorName = getDefaultInstructorName(order)
  const locationName = getLocationName(order)

  const vatRate = client?.defaultVAT ?? undefined
  const vatMultiplier = vatRate !== undefined ? 1 + vatRate / 100 : 1
  const netAmountInCents = vatRate !== undefined ? Math.round(order.price / vatMultiplier) : order.price
  const vatAmountInCents = vatRate !== undefined ? order.price - netAmountInCents : 0

  const currency = client ? getCurrencyForClient(client) : undefined
  const { showSsnInput, setShowSsnInput, savedSsn, isSavingSsn, ssnFormProps, handleSaveSsn } = useReceiptSsn(
    (ssn) => addSsnForPrivateLessonOrder({ clientId: order.clientId, orderId: order.id, ssn }),
    currency
  )

  const handleDownloadPDF = useCallback(
    () =>
      downloadReceiptPdf(
        RECEIPT_ELEMENT_ID,
        `${client?.name || order.schoolName || 'Private_Lessons'}_Order_Receipt_${order.id}.pdf`
      ),
    [order.id, order.schoolName, client?.name]
  )

  return (
    <ReceiptContainer>
      <BackButton onClick={onBack}>
        <BackIcon>←</BackIcon>
        {getText('portalBackToOrders')}
      </BackButton>

      <ReceiptTopRow>
        <ReceiptHeader>
          <ReceiptTitle>{getText('portalOrderReceipt')}</ReceiptTitle>
        </ReceiptHeader>
        <ReceiptActions>
          <SsnToggle type="button" onClick={() => setShowSsnInput(!showSsnInput)}>
            <img src={savedSsn ? penIcon : plusIcon} width={16} height={16} />
            <GrooverText
              textKey={savedSsn ? 'registrationCtactaEditPersonalNumber' : 'registrationCtactaAddPersonalNumber'}
            />
          </SsnToggle>
          <DownloadPdfButton onClick={handleDownloadPDF}>{getText('portalDownloadPdf')}</DownloadPdfButton>
        </ReceiptActions>
      </ReceiptTopRow>

      {showSsnInput && (
        <SsnInputRow>
          <ReceiptSsnInputForm
            control={ssnFormProps.control}
            handleSubmit={ssnFormProps.handleSubmit}
            formState={ssnFormProps.formState}
            isSaving={isSavingSsn}
            onSave={handleSaveSsn}
          />
        </SsnInputRow>
      )}

      <ReceiptCard id={RECEIPT_ELEMENT_ID}>
        <ReceiptRef>#{order.id}</ReceiptRef>
        {client && (client.name || client.orgNo || client.address) && (
          <SellerSection>
            <SellerLabel>{getText('portalReceiptSeller')}</SellerLabel>
            <SellerValue>
              {client.name && <span>{client.name}</span>}
              {client.orgNo && <span>{client.orgNo}</span>}
              {client.address && <span>{client.address}</span>}
            </SellerValue>
          </SellerSection>
        )}
        <ReceiptBody>
          <ReceiptRow>
            <ReceiptRowLabel>{getText('componentsReceiptReceiptPaymentDateLabel')}</ReceiptRowLabel>
            <ReceiptRowValue>{formatDate(order.createdAt)}</ReceiptRowValue>
          </ReceiptRow>
          <ReceiptRow>
            <ReceiptRowLabel>{getText('portalReceiptPackage')}</ReceiptRowLabel>
            <ReceiptRowValue>{order.packageName || getText('portalPrivateLessonPackage')}</ReceiptRowValue>
          </ReceiptRow>
          <ReceiptRow>
            <ReceiptRowLabel>{getText('portalReceiptLessons')}</ReceiptRowLabel>
            <ReceiptRowValue>
              {order.lessonCount}
              <span className="hide-in-pdf">
                {' '}
                ({formatGrooverText(getText('portalReceiptLessonsUsed'), [order.lessonsUsed])})
              </span>
            </ReceiptRowValue>
          </ReceiptRow>
          {instructorName && (
            <ReceiptRow className="hide-in-pdf">
              <ReceiptRowLabel>{getText('portalReceiptInstructor')}</ReceiptRowLabel>
              <ReceiptRowValue>{instructorName}</ReceiptRowValue>
            </ReceiptRow>
          )}
          {locationName && (
            <ReceiptRow>
              <ReceiptRowLabel>{getText('portalReceiptLocation')}</ReceiptRowLabel>
              <ReceiptRowValue>{locationName}</ReceiptRowValue>
            </ReceiptRow>
          )}
          {vatRate !== undefined ? (
            <>
              <ReceiptRow>
                <ReceiptRowLabel>{getText('portalReceiptNetAmountExclVat')}</ReceiptRowLabel>
                <ReceiptRowValue>{formatPrice(netAmountInCents, order.currency)}</ReceiptRowValue>
              </ReceiptRow>
              <ReceiptRow>
                <ReceiptRowLabel>
                  {formatGrooverText(getText('componentsReceiptReceiptPaymentVat'), [vatRate])}
                </ReceiptRowLabel>
                <ReceiptRowValue>{formatPrice(vatAmountInCents, order.currency)}</ReceiptRowValue>
              </ReceiptRow>
              <ReceiptRow>
                <ReceiptRowLabel>{getText('portalReceiptTotalInclVat')}</ReceiptRowLabel>
                <ReceiptRowValue>{formatPrice(order.price, order.currency)}</ReceiptRowValue>
              </ReceiptRow>
            </>
          ) : (
            <ReceiptRow>
              <ReceiptRowLabel>{getText('portalReceiptAmount')}</ReceiptRowLabel>
              <ReceiptRowValue>{formatPrice(order.price, order.currency)}</ReceiptRowValue>
            </ReceiptRow>
          )}
          <ReceiptRow>
            <ReceiptRowLabel>{getText('portalReceiptPaymentMethod')}</ReceiptRowLabel>
            <ReceiptRowValue>{order.paymentMethod}</ReceiptRowValue>
          </ReceiptRow>
          <ReceiptRow className="hide-in-pdf">
            <ReceiptRowLabel>{getText('portalReceiptStatus')}</ReceiptRowLabel>
            <ReceiptRowValue>
              <StatusBadge status={order.status}>{getStatusLabel(order.status, getText)}</StatusBadge>
            </ReceiptRowValue>
          </ReceiptRow>
          {order.validUntil && (
            <ReceiptRow>
              <ReceiptRowLabel>{getText('portalReceiptValidUntil')}</ReceiptRowLabel>
              <ReceiptRowValue>{formatDate(order.validUntil)}</ReceiptRowValue>
            </ReceiptRow>
          )}
          {savedSsn && (
            <ReceiptRow>
              <ReceiptRowLabel>
                <GrooverText textKey="receiptPersonalNumberLabel" />
              </ReceiptRowLabel>
              <ReceiptRowValue>{savedSsn}</ReceiptRowValue>
            </ReceiptRow>
          )}
        </ReceiptBody>
      </ReceiptCard>
    </ReceiptContainer>
  )
}

// Main component
export const PortalHomePage: React.FC = () => {
  const { portalUser, portalClaimsReady } = usePortalAuth()
  const { state } = useAppState()
  const selectedLanguage = state.selectedLanguage
  const getText = useCallback((key: string) => getGrooverText(key, selectedLanguage), [selectedLanguage])
  const isMounted = useMounted()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const [searchParams] = useSearchParams()
  const orderIdFromQuery = searchParams.get('orderId')
  const { orderId: orderIdParam } = useParams<{ orderId?: string }>()
  const [selectedOrderClient, setSelectedOrderClient] = useState<Client | null>(null)

  const enrollmentUserId = portalClaimsReady ? (portalUser?.enrollmentUserId ?? null) : null
  const clientId = usePortalQueryClientId()

  // Fetch user's private lesson orders and occasions using reactive hooks
  const {
    data: orders,
    isLoading: ordersLoading,
    error: ordersError
  } = usePrivateLessonOrdersForUser(enrollmentUserId, clientId)

  const {
    data: occasions,
    isLoading: occasionsLoading,
    error: occasionsError
  } = usePrivateLessonsForUser(enrollmentUserId, clientId)

  // Derive view state from URL
  const activeTab = pathname.includes('occasions') ? 'occasions' : 'orders'
  const selectedOrder = orderIdParam && orders ? (orders.find((o) => o.id === orderIdParam) ?? null) : null
  const filterOrderForOccasions =
    orderIdFromQuery && orders ? (orders.find((o) => o.id === orderIdFromQuery) ?? null) : null

  // Redirect to orders list when orderId in URL is invalid (not in user's orders)
  useEffect(() => {
    if (ordersLoading || !orderIdParam) return
    if (orders && !orders.some((o) => o.id === orderIdParam)) {
      navigate(buildRoute(RoutePath.PORTAL_HOME_ORDERS), { replace: true })
    }
  }, [orders, ordersLoading, orderIdParam, navigate])

  // Fetch client (seller) when viewing a receipt, for payment receiver block on PDF
  useEffect(() => {
    if (!selectedOrder) {
      setSelectedOrderClient(null)
      return
    }
    let cancelled = false
    getClient(selectedOrder.clientId, () => isMounted() && !cancelled)
      .then((clientData) => {
        if (isMounted() && !cancelled && clientData) {
          setSelectedOrderClient(clientData)
        }
      })
      .catch(() => {
        if (isMounted() && !cancelled) {
          setSelectedOrderClient(null)
        }
      })
    return () => {
      cancelled = true
    }
  }, [selectedOrder?.id, selectedOrder?.clientId, isMounted])

  const handleViewReceipt = (order: PrivateLessonOrder) => {
    navigate(buildRoute(RoutePath.PORTAL_HOME_ORDER_RECEIPT, { orderId: order.id }))
  }

  const handleBackToList = () => {
    navigate(buildRoute(RoutePath.PORTAL_HOME_ORDERS))
  }

  const handleViewOccasions = (order: PrivateLessonOrder) => {
    navigate(buildRoute(RoutePath.PORTAL_HOME_OCCASIONS, { orderId: order.id }))
  }

  const handleBackToAllOccasions = () => {
    navigate(buildRoute(RoutePath.PORTAL_HOME_OCCASIONS))
  }

  // Show loading state if either query is loading
  const loading = ordersLoading || occasionsLoading

  // Show error if either query has an error
  const error = ordersError || occasionsError

  if (loading) {
    return (
      <PageContainer>
        <LoadingContainer>
          <LoadingSpinner />
          <LoadingText>Loading your private lessons...</LoadingText>
        </LoadingContainer>
      </PageContainer>
    )
  }

  if (error) {
    return (
      <PageContainer>
        <ErrorContainer>
          <ErrorIcon>⚠️</ErrorIcon>
          <ErrorText>{getText('portalFailedToLoadPrivateLessons')}</ErrorText>
          <RetryButton onClick={() => window.location.reload()}>{getText('portalTryAgain')}</RetryButton>
        </ErrorContainer>
      </PageContainer>
    )
  }

  return (
    <PageContainer>
      <MainContent>
        {selectedOrder ? (
          <OrderReceiptView
            order={selectedOrder}
            client={selectedOrderClient}
            onBack={handleBackToList}
            getText={getText}
          />
        ) : (
          <>
            <PageTitle>{getText('portalYourPrivateLessons')}</PageTitle>
            <TabsContainer>
              <Tab active={activeTab === 'orders'} onClick={() => navigate(buildRoute(RoutePath.PORTAL_HOME_ORDERS))}>
                {getText('portalOrders')}
              </Tab>
              <Tab
                active={activeTab === 'occasions'}
                onClick={() => navigate(buildRoute(RoutePath.PORTAL_HOME_OCCASIONS))}
              >
                {getText('portalOccasions')}
              </Tab>
            </TabsContainer>

            {activeTab === 'orders' && (
              <OrdersTab
                orders={orders}
                loading={ordersLoading}
                error={ordersError}
                onViewReceipt={handleViewReceipt}
                onViewOccasions={handleViewOccasions}
                getText={getText}
              />
            )}

            {activeTab === 'occasions' && (
              <OccasionsTab
                occasions={occasions}
                loading={occasionsLoading}
                error={occasionsError}
                filterOrder={filterOrderForOccasions ?? undefined}
                onBackToAllOccasions={filterOrderForOccasions ? handleBackToAllOccasions : undefined}
                getText={getText}
              />
            )}
          </>
        )}
      </MainContent>
    </PageContainer>
  )
}
